import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form, useField } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import { useDataProvider, useGetMany, useNotify, useRefresh } from 'react-admin';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Theme } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import CloseIcon from '@material-ui/icons/Close';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { Dialog } from 'components/common';
import { Input, Switch } from 'components/form';
import {
  composeValidators,
  greaterThanZero,
  maxValue,
  required,
} from 'components/form/validationRules';
import NumberInputFieldWithActions from 'components/inputs/NumberInputFieldWithActions';

import ImageInputField from '../levels/ImageInputField';
import { RewardBadgeDto, RewardedActionDto } from '../../types';

const useStyles = makeStyles((theme: Theme) => ({
  content: {
    minWidth: theme.spacing(80),
    [theme.breakpoints.down('sm')]: {
      minWidth: 'auto',
    },
  },
  root: {
    maxWidth: '720px',
  },
  layout: {
    display: 'flex',
    gap: '32px',
    flexWrap: 'wrap',
  },
  fieldsColumn: {
    flex: 1,
    minWidth: 200,
    paddingTop: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  numberFields: {
    display: 'flex',
    gap: '16px',
  },
  inputNoMargin: {
    marginTop: 0,
  },
  autocompleteInput: {
    width: '100%',
    // Keep the filled variant but force a white background to match the dialog.
    '& .MuiFilledInput-root': {
      backgroundColor: theme.palette.common.white,
    },
    '& .MuiFilledInput-root:hover': {
      backgroundColor: theme.palette.common.white,
    },
    '& .MuiFilledInput-root.Mui-focused': {
      backgroundColor: theme.palette.common.white,
    },
  },
  autocompletePopper: {
    zIndex: theme.zIndex.modal + 1,
  },
}));

interface EditBadgeDialogProps {
  open: boolean;
  onClose: () => void;
  badge?: RewardBadgeDto;
}

interface FormValues {
  icon?: File | string | null;
  title: string;
  description: string;
  notificationTitle: string;
  notificationBody: string;
  actions: RewardedActionDto['id'][];
  minActionCount: string;
  points: string;
  shipmentBased?: boolean;
}

interface RewardActionOption {
  id?: RewardedActionDto['id'];
  title?: string;
  name?: string;
}

interface ActionsMultiAutocompleteFieldProps {
  label: string;
  className?: string;
  popperClassName?: string;
}

const ACTIONS_AUTOCOMPLETE_DEBOUNCE_MS = 400;
const ACTIONS_AUTOCOMPLETE_PER_PAGE = 100;

const getActionLabel = (choice: RewardActionOption | null | undefined) =>
  choice?.title ?? choice?.name ?? (choice?.id ? String(choice.id) : '');

const mergeActionChoices = (
  choices: RewardActionOption[],
  selectedChoices: RewardActionOption[]
) => {
  // Keep selected items in the options list even when the server filter changes.
  const merged = new Map<string, RewardActionOption>();

  choices.forEach((choice) => {
    if (choice?.id) {
      merged.set(choice.id, choice);
    }
  });

  selectedChoices.forEach((choice) => {
    if (choice?.id) {
      merged.set(choice.id, choice);
    }
  });

  return Array.from(merged.values());
};

const ActionsMultiAutocompleteField = ({
  label,
  className,
  popperClassName,
}: ActionsMultiAutocompleteFieldProps) => {
  const {
    input: { name, value, onChange, onBlur, onFocus },
    meta: { touched, error, submitError, submitFailed, dirtySinceLastSubmit },
  } = useField<FormValues['actions']>('actions', { validate: required });
  const dataProvider = useDataProvider();
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [choices, setChoices] = useState<RewardActionOption[]>([]);
  const [loading, setLoading] = useState(false);
  const requestRef = useRef(0);

  const selectedIds = useMemo(
    () =>
      Array.isArray(value)
        ? value.filter((id): id is string => typeof id === 'string' && Boolean(id))
        : [],
    [value]
  );

  const { data: selectedChoices = [] } = useGetMany('rewardsAction', selectedIds);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, ACTIONS_AUTOCOMPLETE_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchText]);

  useEffect(() => {
    let isMounted = true;
    const currentRequestId = requestRef.current + 1;
    requestRef.current = currentRequestId;

    const fetchActions = async () => {
      setLoading(true);

      try {
        const { data } = await dataProvider.getList('rewardsAction', {
          pagination: { page: 1, perPage: ACTIONS_AUTOCOMPLETE_PER_PAGE },
          sort: { field: 'id', order: 'ASC' },
          filter: debouncedSearchText ? { search: debouncedSearchText } : {},
        });

        if (!isMounted || currentRequestId !== requestRef.current) {
          return;
        }

        setChoices(Array.isArray(data) ? data : []);
      } catch (error) {
        if (!isMounted || currentRequestId !== requestRef.current) {
          return;
        }

        setChoices([]);
      } finally {
        if (isMounted && currentRequestId === requestRef.current) {
          setLoading(false);
        }
      }
    };

    fetchActions();

    return () => {
      isMounted = false;
    };
  }, [dataProvider, debouncedSearchText]);

  const mergedChoices = useMemo(
    () => mergeActionChoices(choices, selectedChoices),
    [choices, selectedChoices]
  );

  const selectedOptions = useMemo(() => {
    const choicesById = new Map<string, RewardActionOption>();

    mergedChoices.forEach((choice) => {
      if (choice?.id) {
        choicesById.set(choice.id, choice);
      }
    });

    return selectedIds.map((id) => choicesById.get(id) || { id, title: id });
  }, [mergedChoices, selectedIds]);

  const shouldShowSubmitError = submitFailed && submitError && !dirtySinceLastSubmit;
  const fieldError =
    (touched || submitFailed) && error ? error : shouldShowSubmitError ? submitError : undefined;
  const hasSearchText = Boolean(searchText);

  const handleClearSearchMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
    // Keep focus in the input while clearing the search query.
    event.preventDefault();
  };

  const handleClearSearch = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setSearchText('');
    setDebouncedSearchText('');
  };

  return (
    <Autocomplete
      multiple
      fullWidth
      disableCloseOnSelect
      disableClearable
      filterSelectedOptions
      filterOptions={(options) => options}
      options={mergedChoices}
      value={selectedOptions}
      loading={loading}
      classes={popperClassName ? { popup: popperClassName } : undefined}
      getOptionLabel={getActionLabel}
      getOptionSelected={(option, selected) => option?.id === selected?.id}
      onFocus={onFocus}
      onBlur={() => onBlur(value)}
      onChange={(_event, newValue) => {
        onChange(
          newValue
            .map((choice) => choice?.id)
            .filter((id): id is string => typeof id === 'string' && Boolean(id))
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          label={label}
          variant="filled"
          fullWidth
          className={className}
          error={Boolean(fieldError)}
          helperText={fieldError}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {hasSearchText ? (
                  <IconButton
                    size="small"
                    aria-label="Clear actions search"
                    onMouseDown={handleClearSearchMouseDown}
                    onClick={handleClearSearch}
                    tabIndex={-1}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ) : null}
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          inputProps={{
            ...params.inputProps,
            value: searchText,
            onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
              const paramsInputProps =
                params.inputProps as React.InputHTMLAttributes<HTMLInputElement>;
              paramsInputProps.onChange?.(event);
              setSearchText(event.target.value);
            },
          }}
        />
      )}
    />
  );
};

const EditBadgeDialog = ({ open, onClose, badge }: EditBadgeDialogProps) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const notify = useNotify();

  const initialValues: FormValues = useMemo(() => {
    // Assumption: badge actions include IDs; missing IDs will skip preselecting actions.
    const actionIds = Array.isArray(badge?.actions)
      ? badge.actions
          .map((action) => (typeof action === 'string' ? action : action?.id))
          .filter((id): id is string => Boolean(id))
      : [];

    return {
      icon: badge?.iconUrl ?? '',
      title: badge?.title ?? '',
      description: badge?.description ?? '',
      notificationTitle: badge?.notificationTitle ?? '',
      notificationBody: badge?.notificationBody ?? '',
      actions: actionIds,
      minActionCount:
        typeof badge?.minActionCount === 'number' ? badge.minActionCount.toString() : '0',
      // Assume the API accepts 0 when points are omitted; adjust if points become required.
      points: typeof badge?.points === 'number' ? badge.points.toString() : '0',
      shipmentBased: badge?.shipmentBased ?? false,
    };
  }, [badge]);

  const handleSubmitForm = async (values: FormValues) => {
    try {
      const formData = new FormData();

      formData.append('title', values.title?.trim() ?? '');
      formData.append('description', values.description?.trim() ?? '');
      formData.append('notificationTitle', values.notificationTitle?.trim() ?? '');
      formData.append('notificationBody', values.notificationBody?.trim() ?? '');
      formData.append('minActionCount', values.minActionCount ?? '0');
      formData.append('points', values.points ?? '0');
      // Assumption: the API accepts boolean values in FormData as 'true'/'false' strings.
      formData.append('shipmentBased', values.shipmentBased ? 'true' : 'false');

      if (values.actions?.length) {
        values.actions.forEach((id) => {
          if (id) {
            formData.append('actions', id);
          }
        });
      } else {
        formData.append('actions', '');
      }

      if (values.icon instanceof File) {
        formData.append('icon', values.icon);
      } else if (typeof values.icon === 'string' && values.icon) {
        formData.append('icon', values.icon);
      }

      const response = badge?.id
        ? await dataProvider.update('rewardsBadge', {
            id: badge.id,
            data: formData,
          })
        : await dataProvider.post('rewardsBadge', {
            data: formData,
          });

      refresh();
      onClose();

      return response;
    } catch (error: any) {
      const message = error?.message || 'Failed to save rewards badge';
      let errorPayload;

      if (typeof error?.message === 'string') {
        try {
          errorPayload = JSON.parse(error.message);
        } catch (parseError) {
          // No-op: fall back to other payload locations.
        }
      }

      if (!errorPayload && error?.body) {
        errorPayload = error.body;
      }

      if (!errorPayload && error?.response?.body) {
        errorPayload = error.response.body;
      }

      const fieldErrors = Array.isArray(errorPayload?.errors)
        ? errorPayload.errors.reduce(
            (acc, err) => {
              if (err?.field) {
                const errorText = err?.description ?? 'Invalid value';
                acc[err.field] = acc[err.field] ? `${acc[err.field]}, ${errorText}` : errorText;
              }
              return acc;
            },
            {} as Record<string, string>
          )
        : {};

      notify(message, 'error');

      if (Object.keys(fieldErrors).length) {
        return { [FORM_ERROR]: message, ...fieldErrors };
      }

      return { [FORM_ERROR]: message };
    }
  };

  if (!open) {
    return null;
  }

  return (
    <Form<FormValues>
      key={badge?.id ?? 'new-badge'}
      initialValues={initialValues}
      onSubmit={handleSubmitForm}
      render={({ handleSubmit, submitting, submitError, values, form }) => {
        // Assumption: "total actions" equals the number of selected actions in the badge.
        const actionsCount = Array.isArray(values?.actions) ? values.actions.length : 0;
        // Shipment-based badges skip the max/adornment; validation should handle any upper bounds.
        const isShipmentBased = Boolean(values?.shipmentBased);
        const shouldSuppressMinActionSubmitError =
          isShipmentBased && Boolean(form.getFieldState('shipmentBased')?.dirtySinceLastSubmit);
        const shouldSuppressPointsSubmitError =
          !isShipmentBased && Boolean(form.getFieldState('shipmentBased')?.dirtySinceLastSubmit);
        const validateMinActionCount = (value, allValues) => {
          const baseError = composeValidators(required, greaterThanZero)(value, allValues);

          if (baseError) {
            return baseError;
          }

          if (Boolean(allValues?.shipmentBased)) {
            return undefined;
          }

          const totalActions = Array.isArray(allValues?.actions) ? allValues.actions.length : 0;
          return maxValue(totalActions)(value);
        };
        const validatePoints = (value, allValues) => {
          const baseError = required(value, allValues);

          if (baseError) {
            return baseError;
          }

          if (Boolean(allValues?.shipmentBased)) {
            return greaterThanZero(value);
          }

          return undefined;
        };
        const handleShipmentBasedChange = (nextValue: boolean) => {
          form.change('shipmentBased', nextValue);
        };

        return (
          <form onSubmit={handleSubmit}>
            <Dialog
              open={open}
              onCancel={onClose}
              title={badge?.id ? 'Edit badge' : 'Add badge'}
              onConfirm={handleSubmit}
              confirmText="Save"
              submitting={submitting}
              error={submitError}
              classes={{ content: classes.content, root: classes.root }}>
              <div className={classes.layout}>
                <ImageInputField name="icon" />
                <div className={classes.fieldsColumn}>
                  <Input
                    name="title"
                    label="Badge name"
                    validate={required}
                    classes={{ input: classes.inputNoMargin }}
                  />
                  <Input
                    name="description"
                    label="Description"
                    validate={required}
                    inputProps={{ multiline: true, rows: 4 }}
                  />
                  <Input name="notificationTitle" label="Notification title" validate={required} />
                  <Input
                    name="notificationBody"
                    label="Notification body"
                    validate={required}
                    inputProps={{ multiline: true, rows: 4 }}
                  />
                  <Switch
                    name="shipmentBased"
                    label="Shipment based"
                    onChange={handleShipmentBasedChange}
                  />
                  {/* Actions are optional for shipment-based badges. */}
                  {!isShipmentBased && (
                    <ActionsMultiAutocompleteField
                      label="Actions"
                      className={classes.autocompleteInput}
                      popperClassName={classes.autocompletePopper}
                    />
                  )}
                  <div className={classes.numberFields}>
                    <NumberInputFieldWithActions
                      name="minActionCount"
                      label="Min Actions Required"
                      validate={validateMinActionCount}
                      isInteger
                      actionButtons
                      suppressSubmitError={shouldSuppressMinActionSubmitError}
                      showValidationErrorOnSubmit
                      endAdornmentText={isShipmentBased ? undefined : `/${actionsCount}`}
                      maxValue={isShipmentBased ? undefined : actionsCount}
                    />
                    <NumberInputFieldWithActions
                      name="points"
                      label="Points"
                      validate={validatePoints}
                      isInteger
                      actionButtons
                      suppressSubmitError={shouldSuppressPointsSubmitError}
                      showValidationErrorOnSubmit
                      startAdornment={null}
                    />
                  </div>
                </div>
              </div>
            </Dialog>
          </form>
        );
      }}
    />
  );
};

export default EditBadgeDialog;
